import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, ChevronDown, Pencil, Share2, Trash2 } from 'lucide-react';
import type { EmployeeDesignation, UserRole } from '../../types';
import ReleaseAudiencePicker from '../release/ReleaseAudiencePicker';
import {
  createGroupTimelineSavedView,
  deleteGroupTimelineSavedView,
  updateGroupTimelineSavedView,
  userCanAccessSavedView,
  type GroupTimelineSavedView,
} from '../../utils/groupTimelineSavedViews';
import './GroupTimelineSavedViewsControl.css';

export interface GroupTimelineSavedViewsControlProps {
  views: GroupTimelineSavedView[];
  activeViewId: string | null;
  currentUserId: string | undefined;
  ownerDisplayName?: string;
  onApply: (view: GroupTimelineSavedView) => void;
  onCaptureOutline: () => import('../../utils/ganttTimelineViewSync').GanttSyncedOutline | null;
  onSaved?: (viewId: string) => void;
}

const GroupTimelineSavedViewsControl: React.FC<GroupTimelineSavedViewsControlProps> = ({
  views,
  activeViewId,
  currentUserId,
  ownerDisplayName = 'User',
  onApply,
  onCaptureOutline,
  onSaved,
}) => {
  const [open, setOpen] = useState(false);
  const [manageView, setManageView] = useState<GroupTimelineSavedView | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const { mine, shared } = useMemo(() => {
    const m: GroupTimelineSavedView[] = [];
    const s: GroupTimelineSavedView[] = [];
    for (const v of views) {
      if (v.ownerUid === currentUserId) m.push(v);
      else s.push(v);
    }
    return { mine: m, shared: s };
  }, [views, currentUserId]);

  const handleSave = async () => {
    const outline = onCaptureOutline();
    if (!outline || outline.mode !== 'all' || !currentUserId) return;
    const name = saveName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const id = await createGroupTimelineSavedView({
        name,
        ownerUid: currentUserId,
        ownerName: ownerDisplayName,
        outline,
      });
      onSaved?.(id);
      setSaveOpen(false);
      setSaveName('');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const saveManage = async () => {
    if (!manageView) return;
    setSaving(true);
    try {
      await updateGroupTimelineSavedView(manageView.id, {
        name: manageView.name,
        audienceUserIds: manageView.audienceUserIds,
        audienceRoles: manageView.audienceRoles,
        audienceDesignations: manageView.audienceDesignations,
      });
      setManageView(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gt-saved-views" ref={rootRef}>
      <button
        type="button"
        className={`gt-saved-views-trigger${open ? ' is-open' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <Bookmark size={13} aria-hidden />
        Views
        <ChevronDown size={12} aria-hidden />
      </button>
      {open ? (
        <div className="gt-saved-views-menu" role="menu">
          <button type="button" className="gt-saved-views-action" onClick={() => setSaveOpen(true)}>
            Save current view…
          </button>
          {mine.length ? <div className="gt-saved-views-heading">My views</div> : null}
          {mine.map(v => (
            <div key={v.id} className="gt-saved-views-row">
              <button
                type="button"
                className={`gt-saved-views-item${activeViewId === v.id ? ' is-active' : ''}`}
                onClick={() => {
                  onApply(v);
                  setOpen(false);
                }}
              >
                {v.name}
              </button>
              <button
                type="button"
                className="gt-saved-views-icon"
                aria-label={`Manage ${v.name}`}
                onClick={() => setManageView({ ...v })}
              >
                <Pencil size={12} />
              </button>
              <button
                type="button"
                className="gt-saved-views-icon"
                aria-label={`Delete ${v.name}`}
                onClick={() => void deleteGroupTimelineSavedView(v.id)}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {shared.length ? <div className="gt-saved-views-heading">Shared with me</div> : null}
          {shared.map(v => (
            <button
              key={v.id}
              type="button"
              className={`gt-saved-views-item${activeViewId === v.id ? ' is-active' : ''}`}
              onClick={() => {
                onApply(v);
                setOpen(false);
              }}
            >
              {v.name}
              <span className="gt-saved-views-by"> · {v.ownerName}</span>
            </button>
          ))}
          {!views.length ? <p className="gt-saved-views-empty">No saved views yet.</p> : null}
        </div>
      ) : null}

      {saveOpen ? (
        <div className="gt-saved-views-modal-backdrop" role="presentation">
          <div className="gt-saved-views-modal" role="dialog" aria-label="Save timeline view">
            <h4>Save timeline view</h4>
            <p>Saves filters, color/layout/sort, and collapse, expand, and pin state for the All tab.</p>
            <input
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="View name"
              autoFocus
            />
            <div className="gt-saved-views-modal-actions">
              <button type="button" className="um-btn-secondary" onClick={() => setSaveOpen(false)}>
                Cancel
              </button>
              <button type="button" className="save-button" disabled={saving || !saveName.trim()} onClick={() => void handleSave()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {manageView && manageView.ownerUid === currentUserId ? (
        <div className="gt-saved-views-modal-backdrop" role="presentation">
          <div className="gt-saved-views-modal gt-saved-views-modal--wide" role="dialog" aria-label="Share timeline view">
            <h4>
              <Share2 size={16} aria-hidden /> Share “{manageView.name}”
            </h4>
            <label>
              Name
              <input
                value={manageView.name}
                onChange={e => setManageView({ ...manageView, name: e.target.value })}
              />
            </label>
            <ReleaseAudiencePicker
              label="Who can use this view?"
              selected={manageView.audienceRoles}
              selectedDesignations={manageView.audienceDesignations}
              selectedUserIds={manageView.audienceUserIds}
              onChange={(roles: UserRole[]) => setManageView({ ...manageView, audienceRoles: roles })}
              onDesignationsChange={(d: EmployeeDesignation[]) =>
                setManageView({ ...manageView, audienceDesignations: d })
              }
              onUserIdsChange={(ids: string[]) => setManageView({ ...manageView, audienceUserIds: ids })}
              includeDesignPartners
            />
            <div className="gt-saved-views-modal-actions">
              <button type="button" className="um-btn-secondary" onClick={() => setManageView(null)}>
                Cancel
              </button>
              <button type="button" className="save-button" disabled={saving} onClick={() => void saveManage()}>
                {saving ? 'Saving…' : 'Save sharing'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GroupTimelineSavedViewsControl;

export { userCanAccessSavedView };
